import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Card,
  InputNumber,
  message,
  Table,
  Row,
  Col,
  Modal,
  Space,
  Upload,
  Image,
  Popconfirm,
  AutoComplete,
  Spin
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  FilePdfOutlined,
  CopyOutlined,
  MailOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileOutlined,
  UploadOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import PayInvoiceButton from "../../../../components/PayInvoiceButton";
import PayPartialButton from "../../../../components/PayPartialButton";
import moment from "moment";
import { shortId } from "helper/util";
import {
  getClients,
  getFactura,
  createFactura,
  updateFacturaThunk,
  deleteFacturaThunk
} from "../store/thunks";
import facturaApi from "../store/api";
import { buildFacturaPdfFilename, downloadPdfResponse } from "../helper";
import routeApi from "../../orders/store/api";
import clientApi from "../../clients/store/api";
import tierApi from "../../tiers/store/api";
import {
  selectClients,
  selectCurrentFactura,
  selectLoading,
  clearCurrentFactura
} from "../store/slice";
import "./FacturaForm.scss";

const { Option } = Select;
const { TextArea } = Input;

// This will be replaced with real data from Redux store

const FacturaForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isEdit, setIsEdit] = useState(!!id);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [currentStatus, setCurrentStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showAddLogModal, setShowAddLogModal] = useState(false);
  const [newLogNote, setNewLogNote] = useState("");
  const [addingLog, setAddingLog] = useState(false);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [editingPrice, setEditingPrice] = useState("");
  const [showPriceExplanationModal, setShowPriceExplanationModal] = useState(false);
  const [priceChangeExplanation, setPriceChangeExplanation] = useState("");
  const [pendingPriceUpdate, setPendingPriceUpdate] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [ccAccounting, setCcAccounting] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [emailingFactura, setEmailingFactura] = useState(false);
  const [showNewChargeModal, setShowNewChargeModal] = useState(false);
  const [newChargeAmount, setNewChargeAmount] = useState("");
  const [newChargeDetail, setNewChargeDetail] = useState("");
  const [addingCharge, setAddingCharge] = useState(false);
  const [showNewEntitlementModal, setShowNewEntitlementModal] = useState(false);
  const [selectedEntitlementId, setSelectedEntitlementId] = useState("");
  const [entitlementAmount, setEntitlementAmount] = useState("");
  const [addingEntitlement, setAddingEntitlement] = useState(false);
  const [availableEntitlements, setAvailableEntitlements] = useState([]);
  const [loadingEntitlements, setLoadingEntitlements] = useState(false);
  const [entitlementSearchValue, setEntitlementSearchValue] = useState("");
  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showAddRoutesModal, setShowAddRoutesModal] = useState(false);
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routeSearchValue, setRouteSearchValue] = useState("");
  const [showAddPoolPackagesModal, setShowAddPoolPackagesModal] = useState(false);
  const [availablePoolPackages, setAvailablePoolPackages] = useState([]);
  const [selectedPoolPackages, setSelectedPoolPackages] = useState([]);
  const [loadingPoolPackages, setLoadingPoolPackages] = useState(false);
  const [poolPackageSearchValue, setPoolPackageSearchValue] = useState("");
  const [packageTypes, setPackageTypes] = useState([]);
  const [showNewTierModal, setShowNewTierModal] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState("");
  const [tierAmount, setTierAmount] = useState("");
  const [addingTier, setAddingTier] = useState(false);
  const [availableTiers, setAvailableTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [tierSearchValue, setTierSearchValue] = useState("");
  const [showAllRoutesModal, setShowAllRoutesModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const totalPaidRef = useRef(null);

  const clients = useSelector(selectClients);
  const currentFactura = useSelector(selectCurrentFactura);
  const loading = useSelector(selectLoading);

  const breadCrumbs = [
    { text: "BILLING", url: "/admin/facturas" },
    { text: "FACTURAS", url: "/admin/facturas" },
    { text: isEdit ? "EDIT INVOICE" : "CREATE INVOICE", url: "" }
  ];

  useEffect(() => {
    // Clear any existing factura data when component mounts
    dispatch(clearCurrentFactura());

    // Load clients data
    dispatch(getClients());

    // Load package types for pool package display
    loadPackageTypes();

    // Load existing factura data if editing
    if (isEdit && id) {
      dispatch(getFactura(id));
    }
  }, [dispatch, isEdit, id]);

  const loadPackageTypes = async () => {
    try {
      const response = await facturaApi.getPackageTypes();
      const types = response?.data || response || [];
      setPackageTypes(types);
    } catch (error) {
      console.error("Error loading package types:", error);
      // Don't show error message to user as this is not critical
    }
  };

  const getPackageTypeName = (packageTypeId) => {
    if (!packageTypeId || packageTypes.length === 0) return "N/A";
    const packageType = packageTypes.find((type) => type.id === packageTypeId);
    return packageType?.name || packageType?.typeName || "N/A";
  };

  useEffect(() => {
    // Populate form with current factura data when it loads
    if (currentFactura && isEdit) {
      const values = {
        facturaId: currentFactura.facturaId,
        facturaDate: moment(currentFactura.facturaDate),
        dueDate: currentFactura.dueDate ? moment(currentFactura.dueDate) : null,
        clientId: currentFactura.clientId,
        status: currentFactura.status,
        total: currentFactura.total,
        totalPaid: currentFactura.totalPaid,
        note: currentFactura.notes || "",
        facturaType: currentFactura.facturaType || "Manual"
      };

      // Check if we're coming from partial status selection
      const urlParams = new URLSearchParams(location.search);
      const fromPartialStatus = urlParams.get("fromPartial") === "true";

      if (fromPartialStatus) {
        // Set status to partial and focus on totalPaid field
        values.status = "partial";
        setCurrentStatus("partial");
        setHasChanges(true); // Enable save button

        // Focus on totalPaid field after a short delay
        setTimeout(() => {
          if (totalPaidRef.current) {
            totalPaidRef.current.focus();
          }
        }, 100);
      } else {
        setCurrentStatus(currentFactura.status);
      }

      setInitialValues(values);
      form.setFieldsValue(values);
    }
  }, [currentFactura, isEdit, form, location.search]);

  // Load factura logs when editing
  const loadLogs = async () => {
    if (!isEdit || !id) return;

    setLogsLoading(true);
    try {
      const response = await facturaApi.getFacturaLogs(id);
      setLogs(response);
    } catch (error) {
      console.error("Error loading logs:", error);
      message.error("Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit && id) {
      loadLogs();
    }
  }, [isEdit, id]);

  // Auto-load routes when modal opens and we have the required data
  useEffect(() => {
    if (
      showAddRoutesModal &&
      (currentFactura?.clientId || form.getFieldValue("clientId"))
    ) {
      console.log("Auto-loading routes due to modal state change");
      loadAvailableRoutes();
    }
  }, [showAddRoutesModal, currentFactura?.clientId]);

  // Auto-load pool packages when modal opens and we have the required data
  useEffect(() => {
    if (
      showAddPoolPackagesModal &&
      (currentFactura?.clientId || form.getFieldValue("clientId"))
    ) {
      console.log("Auto-loading pool packages due to modal state change");
      loadAvailablePoolPackages();
    }
  }, [showAddPoolPackagesModal, currentFactura?.clientId]);

  // Auto-load entitlements when modal opens and we have the required data
  useEffect(() => {
    if (
      showNewEntitlementModal &&
      (currentFactura?.clientId || form.getFieldValue("clientId"))
    ) {
      console.log("Auto-loading entitlements due to modal state change");
      loadAvailableEntitlements();
    }
  }, [showNewEntitlementModal, currentFactura?.clientId]);

  const handleAddLog = async () => {
    if (!newLogNote.trim()) {
      message.error("Please enter a note");
      return;
    }

    setAddingLog(true);
    try {
      await facturaApi.addFacturaLog(id, newLogNote.trim());
      message.success("Log added successfully");
      setNewLogNote("");
      setShowAddLogModal(false);
      // Refresh the page as requested
      window.location.reload();
    } catch (error) {
      console.error("Error adding log:", error);
      message.error("Failed to add log");
    } finally {
      setAddingLog(false);
    }
  };

  const generateFacturaId = (date, clientId) => {
    const dateStr = moment(date).format("YYYY-MM-DD");
    const clientPrefix = clientId.substring(0, 8);
    return `${dateStr}-${clientPrefix}-01`;
  };

  const handleValuesChange = (changedValues, allValues) => {
    setHasChanges(true);

    // Handle status changes
    if (changedValues.status) {
      setCurrentStatus(changedValues.status);

      if (changedValues.status === "paid") {
        // Auto-populate totalPaid with total amount
        const totalAmount = allValues.total || form.getFieldValue("total");
        if (totalAmount) {
          form.setFieldsValue({ totalPaid: totalAmount });
        }
      } else if (
        changedValues.status === "unpaid" ||
        changedValues.status === "overdue" ||
        changedValues.status === "cancelled"
      ) {
        // Reset totalPaid for unpaid/overdue/cancelled
        form.setFieldsValue({ totalPaid: 0 });
      }
    }
  };

  const handleSubmit = async (values) => {
    // Prevent double submission
    if (saving) {
      console.log("Submission already in progress, ignoring");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        // Update existing factura
        const updateData = {
          facturaId: values.facturaId,
          facturaDate: values.facturaDate.format("YYYY-MM-DD"),
          status: values.status,
          total: values.total,
          totalPaid: values.totalPaid,
          notes: values.note,
          facturaType: values.facturaType,
          dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : null
        };

        await dispatch(updateFacturaThunk({ id, data: updateData }));
        message.success("Factura updated successfully!");
        // Stay on the same page by pushing to the current edit URL
        history.push(`/admin/facturas/edit/${id}`);
      } else {
        // Create new factura - removing facturaId as it's now generated server-side
        const createData = {
          facturaDate: values.facturaDate.format("YYYY-MM-DD"),
          dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : undefined,
          clientId: values.clientId,
          total: values.total,
          notes: values.note,
          status: "unpaid",
          facturaType: values.facturaType
        };

        console.log("Creating factura with data:", createData);
        const result = await dispatch(createFactura(createData)).unwrap();
        console.log("Factura creation result:", result);
        message.success("Factura created successfully!");
        // Navigate to the edit page with the newly created factura UUID (not the human-readable facturaId)
        const newId = result.id;
        console.log(
          "Extracted factura UUID:",
          newId,
          "Human-readable ID:",
          result.facturaId
        );
        if (newId) {
          // Use window.location to force a full page reload with the new factura
          window.location.href = `/admin/facturas/edit/${newId}`;
        } else {
          console.error("No UUID returned from create, result:", result);
          history.push("/admin/facturas");
        }
      }
    } catch (error) {
      message.error(error.message || "Failed to save factura");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    history.push("/admin/facturas");
  };

  const handleDelete = async () => {
    if (deleting) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this factura? This action cannot be undone."
    );

    if (!confirmed) return;

    setDeleting(true);
    try {
      await dispatch(deleteFacturaThunk(id));
      message.success("Factura deleted successfully!");
      history.push("/admin/facturas");
    } catch (error) {
      message.error(error.message || "Failed to delete factura");
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const handlePaymentSuccess = () => {
    // Refetch factura data to get updated status
    if (isEdit && id) {
      dispatch(getFactura(id));
      loadLogs(); // Refresh logs to show payment activity
    }
  };

  const handleStartEditPrice = (routeId, currentPrice) => {
    setEditingRouteId(routeId);
    setEditingPrice(currentPrice.toString());
  };

  const handleCancelEditPrice = () => {
    setEditingRouteId(null);
    setEditingPrice("");
  };

  const handleSavePrice = (routeId, newPrice) => {
    if (!newPrice || isNaN(parseFloat(newPrice))) {
      message.error("Please enter a valid price");
      return;
    }

    // Store the pending update and show explanation modal
    setPendingPriceUpdate({
      routeId,
      price: parseFloat(newPrice),
      type: "clientFinalPrice"
    });
    setShowPriceExplanationModal(true);
  };

  const handlePriceExplanationSubmit = async () => {
    if (!priceChangeExplanation.trim()) {
      message.error("Price change explanation is required!");
      return;
    }

    try {
      // Update route price via API
      await routeApi.updatePrice({
        id: pendingPriceUpdate.routeId,
        price: pendingPriceUpdate.price,
        type: pendingPriceUpdate.type,
        priceChangeExplanation: priceChangeExplanation.trim(),
        clientName:
          selectedClient?.clientName || currentFactura?.client?.clientName || "Unknown",
        user: {} // Empty user object as it will be filled by backend from auth
      });

      message.success("Route price updated successfully!");

      // Reset editing state
      setEditingRouteId(null);
      setEditingPrice("");
      setPriceChangeExplanation("");
      setShowPriceExplanationModal(false);
      setPendingPriceUpdate(null);

      // Refresh factura data to see updated totals
      if (isEdit && id) {
        dispatch(getFactura(id));
        loadLogs(); // Refresh logs to show price change activity
      }
    } catch (error) {
      console.error("Error updating route price:", error);
      message.error(error.response?.data?.message || "Failed to update route price");
    }
  };

  const handlePriceExplanationCancel = () => {
    setPriceChangeExplanation("");
    setShowPriceExplanationModal(false);
    setPendingPriceUpdate(null);
    // Keep the editing state so user can try again
  };

  const handleExportFacturaPdf = async () => {
    if (!isEdit || !id) return;

    try {
      const response = await facturaApi.exportFacturaPdf(id);
      const blob = new Blob([response], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.target = "_blank";
      const facturaIdShort = currentFactura?.facturaId || shortId(id);
      link.download = `factura-${facturaIdShort}-${new Date().getTime()}.pdf`;
      window.open(link.href, "_blank");
    } catch (err) {
      console.log(err);
      message.error("Failed to export PDF");
    }
  };

  const handleDownloadFacturaPdf = async () => {
    if (!isEdit || !id) return;

    setDownloadingPdf(true);
    try {
      const response = await facturaApi.exportFacturaPdf(id);
      downloadPdfResponse(
        response,
        buildFacturaPdfFilename(currentFactura?.facturaId || shortId(id))
      );
    } catch (err) {
      console.log(err);
      message.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCopySystemId = async () => {
    if (currentFactura?.id) {
      try {
        await navigator.clipboard.writeText(currentFactura.id);
        message.success("System Factura ID copied to clipboard");
      } catch (err) {
        console.error("Failed to copy:", err);
        message.error("Failed to copy to clipboard");
      }
    }
  };

  const handleEmailFactura = () => {
    if (!currentFactura || !selectedClient) {
      message.error("Cannot email factura: missing client information");
      return;
    }

    // Build default email list
    const defaultEmails = [];
    if (selectedClient.accountingEmail) {
      defaultEmails.push(selectedClient.accountingEmail);
    }
    if (
      selectedClient.supportEmail &&
      selectedClient.supportEmail !== selectedClient.accountingEmail
    ) {
      defaultEmails.push(selectedClient.supportEmail);
    }

    setSelectedEmails(defaultEmails);
    setShowEmailModal(true);
  };

  const handleAddEmail = () => {
    if (!newEmail.trim()) {
      message.error("Please enter a valid email address");
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      message.error("Please enter a valid email address");
      return;
    }

    if (selectedEmails.includes(newEmail.trim())) {
      message.error("Email already added");
      return;
    }

    setSelectedEmails([...selectedEmails, newEmail.trim()]);
    setNewEmail("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setSelectedEmails(selectedEmails.filter((email) => email !== emailToRemove));
  };

  const handleSendEmail = async () => {
    if (selectedEmails.length === 0) {
      message.error("Please select at least one email address");
      return;
    }

    setEmailingFactura(true);
    try {
      await facturaApi.emailFactura(currentFactura.id, {
        to: selectedEmails,
        ccAccounting: ccAccounting
      });
      message.success("Factura email sent successfully!");
      setShowEmailModal(false);
      setSelectedEmails([]);
      setCcAccounting(true);
      setNewEmail("");
      // Refresh logs to show the email log entry
      loadLogs();
    } catch (error) {
      console.error("Error sending factura email:", error);
      message.error(error.response?.data?.message || "Failed to send factura email");
      // Refresh logs to show the error log entry
      loadLogs();
    } finally {
      setEmailingFactura(false);
    }
  };

  const handleEmailModalCancel = () => {
    setShowEmailModal(false);
    setSelectedEmails([]);
    setCcAccounting(true);
    setNewEmail("");
  };

  const handleAddCharge = async () => {
    if (!newChargeAmount || !newChargeDetail.trim()) {
      message.error("Please fill in all required fields");
      return;
    }

    setAddingCharge(true);
    try {
      // Prepare charge data
      const chargeData = {
        description: newChargeDetail.trim(),
        amount: parseFloat(newChargeAmount)
      };

      // Get current total from form
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;

      // Add charge via dedicated API endpoint
      await facturaApi.addChargeToFactura(id, chargeData, currentTotal);

      message.success("Charge added successfully!");

      // Reset modal state
      setShowNewChargeModal(false);
      setNewChargeAmount("");
      setNewChargeDetail("");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error adding charge:", error);
      message.error(error.response?.data?.message || "Failed to add charge");
    } finally {
      setAddingCharge(false);
    }
  };

  const loadAvailablePoolPackages = async () => {
    const clientId = currentFactura?.clientId || form.getFieldValue("clientId");
    if (!clientId) {
      message.error("Client ID not found. Please save the factura first.");
      return;
    }

    setLoadingPoolPackages(true);
    try {
      console.log("Loading available pool packages for client:", clientId);
      const response = await facturaApi.getUnassignedPoolPackagesForClient(clientId);
      console.log("Pool packages API response:", response);

      // Handle different response structures
      let poolPackages = response;
      if (response && response.data) {
        poolPackages = response.data;
      }
      // Handle paginated response from nestjs-typeorm-paginate
      if (response && response.items) {
        poolPackages = response.items;
      }

      // Filter out cancelled items and only include current/previous month
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;

      const filteredPackages = (poolPackages || []).filter((pkg) => {
        // Filter out cancelled
        if (pkg.status === "cancelled") return false;

        // Filter by date (current or previous month)
        if (pkg.createdAt) {
          const pkgDate = new Date(pkg.createdAt);
          const pkgMonth = pkgDate.getMonth();
          const pkgYear = pkgDate.getFullYear();

          return (
            (pkgYear === currentYear && pkgMonth === currentMonth) ||
            (pkgYear === previousYear && pkgMonth === previousMonth)
          );
        }

        return true; // Include if no date available
      });

      console.log("Pool packages filtered:", filteredPackages);
      if (filteredPackages.length > 0) {
        console.log("Sample pool package data:", filteredPackages[0]);
        console.log("Available package fields:", Object.keys(filteredPackages[0]));
      }
      setAvailablePoolPackages(Array.isArray(filteredPackages) ? filteredPackages : []);
    } catch (error) {
      console.error("Error loading pool packages:", error);
      message.error("Failed to load available pool packages");
      setAvailablePoolPackages([]);
    } finally {
      setLoadingPoolPackages(false);
    }
  };

  const handleAddPoolPackages = async () => {
    if (!selectedPoolPackages || selectedPoolPackages.length === 0) {
      message.error("Please select at least one pool package");
      return;
    }

    try {
      // Get current total from form
      const currentTotal = parseFloat(
        form.getFieldValue("total") || currentFactura?.total || 0
      );

      // Calculate total of selected pool packages
      const selectedPackagesTotal = selectedPoolPackages.reduce((sum, packageId) => {
        const pkg = availablePoolPackages.find((p) => p.id === packageId);
        console.log("Pool package for total calculation:", pkg);
        const price = parseFloat(
          pkg?.packageV2?.price || pkg?.amount || pkg?.price || pkg?.cost || 0
        );
        console.log("Package price:", price);
        return sum + price;
      }, 0);

      console.log("Current total:", currentTotal);
      console.log("Selected packages total:", selectedPackagesTotal);
      console.log("New total should be:", currentTotal + selectedPackagesTotal);

      // Add pool packages to factura via dedicated API endpoint
      await facturaApi.addPoolPackagesToFactura(id, selectedPoolPackages, currentTotal);

      message.success("Pool packages added successfully!");

      // Reset modal state
      setShowAddPoolPackagesModal(false);
      setSelectedPoolPackages([]);

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();

      // Manually update the form total as a fallback
      setTimeout(() => {
        // Get the updated factura data from Redux store
        const updatedFactura = currentFactura;
        const backendTotal = parseFloat(updatedFactura?.total || 0);

        // If backend didn't preserve the total correctly, manually set it
        if (selectedPackagesTotal > 0) {
          const expectedTotal = currentTotal + selectedPackagesTotal;
          const actualFormTotal = parseFloat(form.getFieldValue("total") || 0);

          console.log("Expected total:", expectedTotal);
          console.log("Backend total:", backendTotal);
          console.log("Form total:", actualFormTotal);

          // If the totals don't match, manually set the correct total
          if (Math.abs(actualFormTotal - expectedTotal) > 0.01) {
            console.log("Manually setting total to:", expectedTotal);
            form.setFieldsValue({ total: expectedTotal });
            setHasChanges(true);
          }
        }
      }, 1000);
    } catch (error) {
      console.error("Error adding pool packages:", error);
      message.error(error.response?.data?.message || "Failed to add pool packages");
    }
  };

  const loadAvailableRoutes = async () => {
    console.log("loadAvailableRoutes called");
    const clientId = currentFactura?.clientId || form.getFieldValue("clientId");
    console.log("Determined client ID:", clientId);
    console.log("currentFactura:", currentFactura);
    console.log("form clientId:", form.getFieldValue("clientId"));

    if (!clientId) {
      console.error("No client ID found");
      message.error("Client ID not found. Please save the factura first.");
      return;
    }

    setLoadingRoutes(true);
    try {
      console.log("Loading available routes for client:", clientId);
      const response = await facturaApi.getRecentUnassignedRoutesForClient(clientId);
      console.log("Routes API response:", response);

      // Handle different response structures
      let routes = response;
      if (response && response.data) {
        routes = response.data;
      }
      // Handle paginated response from nestjs-typeorm-paginate
      if (response && response.items) {
        routes = response.items;
      }

      // Filter out cancelled items and only include current/previous month
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;

      const filteredRoutes = (routes || []).filter((route) => {
        // Filter out cancelled
        if (route.status === "cancelled") return false;

        // Filter by date (current or previous month)
        if (route.createdAt) {
          const routeDate = new Date(route.createdAt);
          const routeMonth = routeDate.getMonth();
          const routeYear = routeDate.getFullYear();

          return (
            (routeYear === currentYear && routeMonth === currentMonth) ||
            (routeYear === previousYear && routeMonth === previousMonth)
          );
        }

        return true; // Include if no date available
      });

      console.log("Routes filtered:", filteredRoutes);
      if (filteredRoutes.length > 0) {
        console.log("Sample route data:", filteredRoutes[0]);
        console.log("Available fields:", Object.keys(filteredRoutes[0]));
      }
      setAvailableRoutes(Array.isArray(filteredRoutes) ? filteredRoutes : []);
    } catch (error) {
      console.error("Error loading routes:", error);
      message.error("Failed to load available routes");
      setAvailableRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  };

  const handleAddRoutes = async () => {
    if (!selectedRoutes || selectedRoutes.length === 0) {
      message.error("Please select at least one route");
      return;
    }

    try {
      // Get current total from form
      const currentTotal = parseFloat(
        form.getFieldValue("total") || currentFactura?.total || 0
      );

      // Calculate total of selected routes
      const selectedRoutesTotal = selectedRoutes.reduce((sum, routeId) => {
        const route = availableRoutes.find((r) => r.id === routeId);
        return sum + parseFloat(route?.clientFinalPrice || 0);
      }, 0);

      console.log("Current total:", currentTotal);
      console.log("Selected routes total:", selectedRoutesTotal);
      console.log("New total should be:", currentTotal + selectedRoutesTotal);

      // Add routes to factura via dedicated API endpoint
      await facturaApi.addRoutesToFactura(id, selectedRoutes, currentTotal);

      message.success("Routes added successfully!");

      // Reset modal state
      setShowAddRoutesModal(false);
      setSelectedRoutes([]);

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();

      // Manually update the form total as a fallback
      setTimeout(() => {
        // Get the updated factura data from Redux store
        const updatedFactura = currentFactura;
        const backendTotal = parseFloat(updatedFactura?.total || 0);

        // If backend didn't preserve the total correctly, manually set it
        if (selectedRoutesTotal > 0) {
          const expectedTotal = currentTotal + selectedRoutesTotal;
          const actualFormTotal = parseFloat(form.getFieldValue("total") || 0);

          console.log("Expected total:", expectedTotal);
          console.log("Backend total:", backendTotal);
          console.log("Form total:", actualFormTotal);

          // If the totals don't match, manually set the correct total
          if (Math.abs(actualFormTotal - expectedTotal) > 0.01) {
            console.log("Manually setting total to:", expectedTotal);
            form.setFieldsValue({ total: expectedTotal });
            setHasChanges(true);
          }
        }
      }, 1000);
    } catch (error) {
      console.error("Error adding routes:", error);
      message.error(error.response?.data?.message || "Failed to add routes");
    }
  };

  const loadAvailableEntitlements = async () => {
    const clientId = currentFactura?.clientId || form.getFieldValue("clientId");
    if (!clientId) {
      message.error("Client ID not found. Please save the factura first.");
      return;
    }

    setLoadingEntitlements(true);
    try {
      console.log("Loading available entitlements for client:", clientId);
      const response = await facturaApi.getAvailableEntitlementsForClient(clientId);
      console.log("Entitlements API response:", response);

      // Handle different response structures
      let entitlements = response;
      if (response && response.data) {
        entitlements = response.data;
      }
      // Handle paginated response from nestjs-typeorm-paginate
      if (response && response.items) {
        entitlements = response.items;
      }

      // Filter only active entitlements that aren't already assigned to this factura
      const activeEntitlements = (entitlements || []).filter(
        (entitlement) => entitlement.isActive !== false
      );

      console.log("Active entitlements filtered:", activeEntitlements);
      setAvailableEntitlements(activeEntitlements);
    } catch (error) {
      console.error("Error loading entitlements:", error);
      message.error("Failed to load available entitlements");
      setAvailableEntitlements([]);
    } finally {
      setLoadingEntitlements(false);
    }
  };

  const handleAddEntitlement = async () => {
    if (!selectedEntitlementId || !entitlementAmount) {
      message.error("Please fill in all required fields");
      return;
    }

    setAddingEntitlement(true);
    try {
      // Find the selected entitlement details
      const selectedEntitlement = availableEntitlements.find(
        (e) => e.id === selectedEntitlementId
      );
      if (!selectedEntitlement) {
        message.error("Selected entitlement not found");
        return;
      }

      // Prepare entitlement data
      const entitlementData = {
        entitlementId: selectedEntitlementId,
        description:
          selectedEntitlement.name || selectedEntitlement.description || "Entitlement",
        amount: parseFloat(entitlementAmount)
      };

      // Get current total from form
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;

      // Add entitlement via dedicated API endpoint
      await facturaApi.addEntitlementToFactura(id, entitlementData, currentTotal);

      message.success("Entitlement added successfully!");

      // Reset modal state
      setShowNewEntitlementModal(false);
      setSelectedEntitlementId("");
      setEntitlementAmount("");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error adding entitlement:", error);
      message.error(error.response?.data?.message || "Failed to add entitlement");
    } finally {
      setAddingEntitlement(false);
    }
  };

  const validateFileType = (file) => {
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/vnd.oasis.opendocument.text",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic"
    ];

    return allowedTypes.includes(file.type);
  };

  const validateFileSize = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    return file.size <= maxSize;
  };

  const beforeUpload = (file) => {
    if (!validateFileType(file)) {
      message.error("Only PDF, XLSX, DOCX, CSV, ODS, ODT, and image files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (!validateFileSize(file)) {
      message.error("File size must be less than 5MB");
      return Upload.LIST_IGNORE;
    }

    setSelectedFile(file);
    setShowNewFileModal(true);
    return false; // Prevent automatic upload
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      message.error("Please select a file");
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      await facturaApi.addFileToFactura(id, formData);
      message.success("File uploaded successfully!");

      // Reset modal state
      setShowNewFileModal(false);
      setSelectedFile(null);

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error uploading file:", error);
      message.error(error.response?.data?.message || "Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileRemove = async (fileId) => {
    try {
      await facturaApi.removeFileFromFactura(id, fileId);
      message.success("File removed successfully!");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error removing file:", error);
      message.error(error.response?.data?.message || "Failed to remove file");
    }
  };

  const handleRemoveRoute = async (routeId) => {
    try {
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;
      await facturaApi.removeRouteFromFactura(id, routeId, currentTotal);
      message.success("Route removed successfully!");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error removing route:", error);
      message.error(error.response?.data?.message || "Failed to remove route");
    }
  };

  const handleRemovePoolPackage = async (packageId) => {
    try {
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;
      await facturaApi.removePoolPackageFromFactura(id, packageId, currentTotal);
      message.success("Pool package removed successfully!");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error removing pool package:", error);
      message.error(error.response?.data?.message || "Failed to remove pool package");
    }
  };

  const handleRemoveEntitlement = async (entitlementId) => {
    try {
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;
      await facturaApi.removeEntitlementFromFactura(id, entitlementId, currentTotal);
      message.success("Entitlement removed successfully!");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error removing entitlement:", error);
      message.error(error.response?.data?.message || "Failed to remove entitlement");
    }
  };

  const loadAvailableTiers = async () => {
    setLoadingTiers(true);
    try {
      console.log("Loading available tiers");
      const response = await tierApi.getTiers();
      console.log("Tiers API response:", response);

      // Handle different response structures
      let tiers = response;
      if (response && response.data) {
        tiers = response.data;
      }

      // Filter only active tiers
      const activeTiers = (tiers || []).filter((tier) => tier.isActive !== false);

      console.log("Active tiers filtered:", activeTiers);
      setAvailableTiers(activeTiers);
    } catch (error) {
      console.error("Error loading tiers:", error);
      message.error("Failed to load available tiers");
      setAvailableTiers([]);
    } finally {
      setLoadingTiers(false);
    }
  };

  const handleAddTier = async () => {
    if (!selectedTierId || !tierAmount) {
      message.error("Please fill in all required fields");
      return;
    }

    setAddingTier(true);
    try {
      // Find the selected tier details
      const selectedTier = availableTiers.find((t) => t.id === selectedTierId);
      if (!selectedTier) {
        message.error("Selected tier not found");
        return;
      }

      // Prepare tier data
      const tierData = {
        tierId: selectedTierId,
        description: selectedTier.name || "Tier",
        amount: parseFloat(tierAmount)
      };

      // Get current total from form
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;

      // Add tier via dedicated API endpoint
      await facturaApi.addTierToFactura(id, tierData, currentTotal);

      message.success("Tier added successfully!");

      // Reset modal state
      setShowNewTierModal(false);
      setSelectedTierId("");
      setTierAmount("");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error adding tier:", error);
      message.error(error.response?.data?.message || "Failed to add tier");
    } finally {
      setAddingTier(false);
    }
  };

  const handleRemoveTier = async (tierId) => {
    try {
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;
      await facturaApi.removeTierFromFactura(id, tierId, currentTotal);
      message.success("Tier removed successfully!");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error removing tier:", error);
      message.error(error.response?.data?.message || "Failed to remove tier");
    }
  };

  const handleRemoveCharge = async (chargeId) => {
    try {
      const currentTotal = form.getFieldValue("total") || currentFactura?.total || 0;
      await facturaApi.removeChargeFromFactura(id, chargeId, currentTotal);
      message.success("Charge removed successfully!");

      // Refresh factura data and logs
      dispatch(getFactura(id));
      loadLogs();
    } catch (error) {
      console.error("Error removing charge:", error);
      message.error(error.response?.data?.message || "Failed to remove charge");
    }
  };

  const renderFileIcon = (mimeType) => {
    if (mimeType.startsWith("image/")) {
      return <FileOutlined style={{ color: "#52c41a" }} />;
    } else if (mimeType === "application/pdf") {
      return <FilePdfOutlined style={{ color: "#f5222d" }} />;
    } else {
      return <FileOutlined style={{ color: "#1890ff" }} />;
    }
  };

  const renderRouteCard = (route, { inModal = false } = {}) => (
    <Card
      key={route.routeId}
      size="small"
      className="route-card"
      style={{
        marginBottom: 12,
        cursor: "pointer",
        border: "1px solid #d9d9d9"
      }}
      onClick={() => {
        if (inModal) {
          setShowAllRoutesModal(false);
        }
        // Navigate to route detail page
        history.push(`/admin/routes/${route.routeId}`);
      }}
      hoverable
    >
      <Row gutter={16} align="middle">
        <Col span={4}>
          <div
            className="route-thumbnail"
            style={{
              width: 60,
              height: 60,
              backgroundColor: "#f0f0f0",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              color: "#666"
            }}
          >
            Route
          </div>
        </Col>
        <Col span={8}>
          <div style={{ fontWeight: 500 }}>{route.routeId.substring(0, 8)}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>Route ID</div>
        </Col>
        <Col span={6}>
          <div style={{ fontWeight: 500 }}>{route.totalStops} stops</div>
          <div style={{ fontSize: "12px", color: "#666" }}>Total Stops</div>
        </Col>
        <Col span={6}>
          {editingRouteId === route.routeId ? (
            <Space onClick={(e) => e.stopPropagation()}>
              <InputNumber
                value={editingPrice}
                onChange={(value) => setEditingPrice(value?.toString() || "")}
                onPressEnter={() => handleSavePrice(route.routeId, editingPrice)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: 80 }}
                size="small"
                precision={2}
                min={0}
              />
              <CheckOutlined
                style={{ color: "green", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSavePrice(route.routeId, editingPrice);
                }}
              />
              <CloseOutlined
                style={{ color: "#ccc", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEditPrice();
                }}
              />
            </Space>
          ) : (
            <Space>
              <div style={{ fontWeight: 500, color: "#52c41a" }}>
                ${route.finalPrice.toFixed(2)}
              </div>
              {currentFactura?.status !== "paid" &&
                currentFactura?.status !== "cancelled" && (
                  <>
                    <EditOutlined
                      style={{
                        color: "#1890ff",
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEditPrice(route.routeId, route.finalPrice);
                      }}
                    />
                    <Popconfirm
                      title="Remove Route"
                      description="Are you sure you want to remove this route from the factura?"
                      onConfirm={(e) => {
                        e?.stopPropagation();
                        handleRemoveRoute(route.routeId);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <DeleteOutlined
                        style={{
                          color: "#ff4d4f",
                          cursor: "pointer",
                          fontSize: "12px",
                          marginLeft: "8px"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Popconfirm>
                  </>
                )}
            </Space>
          )}
          <div style={{ fontSize: "12px", color: "#666" }}>Final Price</div>
        </Col>
      </Row>
    </Card>
  );

  const selectedClient = Array.isArray(clients)
    ? clients.find((c) => c.id === form.getFieldValue("clientId"))
    : null;
  const previewFacturaId =
    form.getFieldValue("facturaDate") && selectedClient
      ? generateFacturaId(form.getFieldValue("facturaDate"), selectedClient.id)
      : "";

  // Define logs table columns
  const logColumns = [
    {
      title: "Date",
      dataIndex: "timestamp",
      width: 100,
      render: (timestamp) => moment(timestamp).format("YYYY-MM-DD")
    },
    {
      title: "User",
      dataIndex: "user",
      width: 100,
      render: (user) => user?.displayName || user?.email || "Unknown"
    },
    {
      title: "Note",
      dataIndex: "summary",
      render: (summary) => summary
    }
  ];

  return (
    <div className="factura-form-page admin">
      <div className="common-header">
        <AdminBreadCrumb breadCrumbs={breadCrumbs} />
        <div style={{ display: "flex", gap: 8 }}>
          {isEdit && currentFactura && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadFacturaPdf}
              loading={downloadingPdf}
              size="small"
            >
              Download PDF
            </Button>
          )}
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack} size="small">
            Back to Facturas
          </Button>
        </div>
      </div>

      <div className="content-wrapper">
        <Row gutter={24}>
          <Col xs={24} lg={isEdit ? 10 : 24}>
            <Card title={isEdit ? "Edit Invoice" : "Create New Invoice"}>
              {isEdit && currentFactura && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#bbb",
                    marginBottom: "16px",
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  System Factura ID: {currentFactura.id}
                  <CopyOutlined
                    style={{
                      cursor: "pointer",
                      color: "#bbb",
                      fontSize: "12px"
                    }}
                    onClick={handleCopySystemId}
                    title="Copy to clipboard"
                  />
                </div>
              )}
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onValuesChange={handleValuesChange}
                className="factura-form"
              >
                {isEdit && (
                  <Form.Item
                    name="facturaId"
                    label="Invoice ID"
                    rules={[{ required: true, message: "Please enter invoice ID" }]}
                  >
                    <Input
                      style={{
                        fontFamily: "monospace",
                        fontSize: "14px"
                      }}
                    />
                  </Form.Item>
                )}
                <div className="form-row">
                  <div className="form-col">
                    <Form.Item
                      name="facturaDate"
                      label="Factura Date"
                      rules={[{ required: true, message: "Please select a date" }]}
                    >
                      <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                    </Form.Item>
                  </div>

                  <div className="form-col">
                    <Form.Item name="dueDate" label="Due Date">
                      <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                    </Form.Item>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <Form.Item
                      name="clientId"
                      label="Client"
                      rules={[{ required: true, message: "Please select a client" }]}
                    >
                      <Select
                        placeholder="Search and select client"
                        showSearch
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={isEdit}
                      >
                        {Array.isArray(clients) &&
                          clients.map((client) => (
                            <Option key={client.id} value={client.id}>
                              {client.clientName}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                </div>

                {previewFacturaId && !isEdit && (
                  <div className="factura-id-preview">
                    <strong>Factura ID: </strong>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: "14px",
                        color: "#1890ff"
                      }}
                    >
                      {previewFacturaId}
                    </span>
                  </div>
                )}

                {isEdit && (
                  <div className="form-row">
                    <div className="form-col">
                      <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: "Please select a status" }]}
                      >
                        <Select placeholder="Select status">
                          <Option value="unpaid">Unpaid</Option>
                          <Option value="partial">Partial</Option>
                          <Option value="paid">Paid</Option>
                          <Option value="overdue">Overdue</Option>
                          <Option value="cancelled">Cancelled</Option>
                        </Select>
                      </Form.Item>
                    </div>

                    <div className="form-col">
                      <Form.Item name="facturaType" label="Type">
                        <Select style={{ width: "100%" }}>
                          <Option value="Managed">Managed</Option>
                          <Option value="SaaS">SaaS</Option>
                          <Option value="Manual">Manual</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                )}

                {!isEdit && (
                  <div className="form-row">
                    <div className="form-col">
                      <Form.Item name="facturaType" label="Type" initialValue="Manual">
                        <Select style={{ width: "100%" }}>
                          <Option value="Managed">Managed</Option>
                          <Option value="SaaS">SaaS</Option>
                          <Option value="Manual">Manual</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-col">
                    <Form.Item name="total" label="Total Due ($)" initialValue={0}>
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        max={99999999.99}
                        precision={2}
                        defaultValue={0}
                        formatter={(value) =>
                          value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                        }
                        parser={(value) => value?.replace(/(,*)/g, "") || ""}
                      />
                    </Form.Item>
                  </div>

                  {isEdit && (
                    <div className="form-col">
                      <Form.Item name="totalPaid" label="Total Paid ($)">
                        <InputNumber
                          ref={totalPaidRef}
                          style={{ width: "100%" }}
                          min={0}
                          max={99999999.99}
                          precision={2}
                          formatter={(value) =>
                            value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                          }
                          parser={(value) => value?.replace(/(,*)/g, "") || ""}
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>

                <Form.Item name="note" label="Notes">
                  <TextArea
                    rows={4}
                    placeholder={
                      currentStatus === "partial"
                        ? "Notes are required for partial payments..."
                        : "Enter any notes or details about this factura..."
                    }
                  />
                </Form.Item>

                {isEdit && currentFactura && (
                  <div style={{ marginBottom: 24, display: "flex", gap: 8 }}>
                    <Button
                      type="default"
                      icon={<FilePdfOutlined />}
                      onClick={handleExportFacturaPdf}
                      size="default"
                    >
                      View PDF
                    </Button>
                    <Button
                      type="default"
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadFacturaPdf}
                      loading={downloadingPdf}
                      size="default"
                    >
                      Download PDF
                    </Button>
                    <Button
                      type="primary"
                      style={{ backgroundColor: "#1e3a8a", borderColor: "#1e3a8a" }}
                      icon={<MailOutlined />}
                      onClick={handleEmailFactura}
                      disabled={!currentFactura?.client}
                    >
                      Email Invoice
                    </Button>
                  </div>
                )}

                <Form.Item className="form-actions">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%"
                    }}
                  >
                    <div style={{ display: "flex", gap: 8 }}>
                      {isEdit && (
                        <Button danger onClick={handleDelete} loading={deleting}>
                          {deleting ? "Deleting..." : "Delete"}
                        </Button>
                      )}
                      {isEdit && currentFactura && (
                        <>
                          <PayInvoiceButton
                            facturaId={currentFactura.id}
                            facturaStatus={currentFactura.status}
                            isClientSide={false}
                            size="default"
                            onSuccess={handlePaymentSuccess}
                            onError={(error) => {
                              console.error("Payment error:", error);
                            }}
                          />
                          <PayPartialButton
                            facturaId={currentFactura.id}
                            totalAmount={parseFloat(currentFactura.total || 0)}
                            paidAmount={parseFloat(currentFactura.totalPaid || 0)}
                            facturaStatus={currentFactura.status}
                            isClientSide={false}
                            size="default"
                            onSuccess={handlePaymentSuccess}
                            onError={(error) => {
                              console.error("Payment error:", error);
                            }}
                          />
                        </>
                      )}
                    </div>
                    <div>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={saving}
                        disabled={isEdit ? !hasChanges : false}
                      >
                        {saving ? "Saving..." : isEdit ? "Save" : "Create Factura"}
                      </Button>
                    </div>
                  </div>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          {isEdit && (
            <Col xs={24} lg={14}>
              <Card
                title="Invoice Logs"
                className="factura-logs-card"
                extra={
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => setShowAddLogModal(true)}
                  >
                    Add Log
                  </Button>
                }
              >
                <Table
                  columns={logColumns}
                  dataSource={logs}
                  loading={logsLoading}
                  size="small"
                  rowKey="id"
                  pagination={{ pageSize: 10, size: "small" }}
                  scroll={{ y: 400 }}
                />
              </Card>
            </Col>
          )}
        </Row>

        {/* Routes Section */}
        {isEdit && currentFactura && (
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                title="Routes"
                className="factura-routes-card"
                extra={
                  currentFactura?.status !== "paid" &&
                  currentFactura?.status !== "cancelled" && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setShowAddRoutesModal(true)}
                    >
                      + Add Routes
                    </Button>
                  )
                }
              >
                {currentFactura.facturaDetails?.routes &&
                currentFactura.facturaDetails.routes.length > 0 ? (
                  <>
                    <div
                      className="routes-summary"
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        background: "#f5f5f5",
                        borderRadius: 4
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <strong>Total Routes: </strong>
                          {currentFactura.facturaDetails.summary?.totalRoutes ||
                            currentFactura.facturaDetails.routes.length}
                        </Col>
                        <Col span={8}>
                          <strong>Total Amount: </strong>$
                          {(
                            currentFactura.facturaDetails.summary?.totalRoutePrice || 0
                          ).toFixed(2)}
                        </Col>
                      </Row>
                    </div>
                    <div className="routes-list">
                      {currentFactura.facturaDetails.routes
                        .slice(0, 5)
                        .map((route) => renderRouteCard(route))}
                    </div>
                    {currentFactura.facturaDetails.routes.length > 5 && (
                      <div style={{ textAlign: "center", marginTop: 16 }}>
                        <Button
                          type="primary"
                          onClick={() => setShowAllRoutesModal(true)}
                        >
                          See All Routes ({currentFactura.facturaDetails.routes.length})
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#999",
                      fontStyle: "italic"
                    }}
                  >
                    No routes
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Pool Packages Section */}
        {isEdit && currentFactura && (
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                title="Pool Packages"
                className="factura-pool-packages-card"
                extra={
                  currentFactura?.status !== "paid" &&
                  currentFactura?.status !== "cancelled" && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setShowAddPoolPackagesModal(true)}
                    >
                      + Add Pool Packages
                    </Button>
                  )
                }
              >
                {currentFactura.facturaDetails?.poolPackages &&
                currentFactura.facturaDetails.poolPackages.length > 0 ? (
                  <>
                    <div
                      className="pool-packages-summary"
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        background: "#f5f5f5",
                        borderRadius: 4
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <strong>Total Pool Packages: </strong>
                          {currentFactura.facturaDetails.summary?.totalPoolPackages ||
                            currentFactura.facturaDetails.poolPackages.length}
                        </Col>
                        <Col span={8}>
                          <strong>Total Amount: </strong>$
                          {(
                            currentFactura.facturaDetails.summary
                              ?.totalPoolPackagePrice || 0
                          ).toFixed(2)}
                        </Col>
                      </Row>
                    </div>
                    <div className="pool-packages-list">
                      {currentFactura.facturaDetails.poolPackages.map((poolPackage) => (
                        <Card
                          key={poolPackage.packageId}
                          size="small"
                          className="pool-package-card"
                          style={{
                            marginBottom: 12,
                            border: "1px solid #d9d9d9"
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col span={4}>
                              <div
                                className="pool-package-thumbnail"
                                style={{
                                  width: 60,
                                  height: 60,
                                  backgroundColor: "#e6f4ff",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "12px",
                                  color: "#1890ff"
                                }}
                              >
                                Package
                              </div>
                            </Col>
                            <Col span={8}>
                              <div style={{ fontWeight: 500 }}>
                                {poolPackage.packageId.substring(0, 8)}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Package ID
                              </div>
                            </Col>
                            <Col span={5}>
                              <div style={{ fontWeight: 500 }}>{poolPackage.status}</div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Status
                              </div>
                            </Col>
                            <Col span={5}>
                              <div style={{ fontWeight: 500, color: "#52c41a" }}>
                                ${(poolPackage.amount || 0).toFixed(2)}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Amount
                              </div>
                            </Col>
                            <Col span={2}>
                              {currentFactura?.status !== "paid" &&
                                currentFactura?.status !== "cancelled" && (
                                  <Popconfirm
                                    title="Remove Pool Package"
                                    description="Are you sure you want to remove this pool package from the factura?"
                                    onConfirm={() =>
                                      handleRemovePoolPackage(poolPackage.packageId)
                                    }
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <DeleteOutlined
                                      style={{
                                        color: "#ff4d4f",
                                        cursor: "pointer",
                                        fontSize: "16px"
                                      }}
                                    />
                                  </Popconfirm>
                                )}
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#999",
                      fontStyle: "italic"
                    }}
                  >
                    No pool packages
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Entitlements Section */}
        {isEdit && currentFactura && (
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                title="Entitlements"
                className="factura-entitlements-card"
                extra={
                  currentFactura?.status !== "paid" &&
                  currentFactura?.status !== "cancelled" && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setShowNewEntitlementModal(true)}
                    >
                      + New Entitlement
                    </Button>
                  )
                }
              >
                {currentFactura.facturaDetails?.entitlements &&
                currentFactura.facturaDetails.entitlements.length > 0 ? (
                  <>
                    <div
                      className="entitlements-summary"
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        background: "#f5f5f5",
                        borderRadius: 4
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <strong>Total Entitlements: </strong>
                          {currentFactura.facturaDetails.entitlements.length}
                        </Col>
                        <Col span={8}>
                          <strong>Total Amount: </strong>$
                          {currentFactura.facturaDetails.entitlements
                            .reduce((sum, item) => sum + (item.amount || 0), 0)
                            .toFixed(2)}
                        </Col>
                      </Row>
                    </div>
                    <div className="entitlements-list">
                      {currentFactura.facturaDetails.entitlements.map(
                        (entitlement, index) => (
                          <Card
                            key={entitlement.id || index}
                            size="small"
                            className="entitlement-card"
                            style={{
                              marginBottom: 12,
                              border: "1px solid #d9d9d9"
                            }}
                          >
                            <Row gutter={16} align="middle">
                              <Col span={4}>
                                <div
                                  className="entitlement-thumbnail"
                                  style={{
                                    width: 60,
                                    height: 60,
                                    backgroundColor: "#e6f7ff",
                                    borderRadius: 4,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    color: "#1890ff"
                                  }}
                                >
                                  Ent
                                </div>
                              </Col>
                              <Col span={9}>
                                <div style={{ fontWeight: 500 }}>
                                  {entitlement.description || "Entitlement"}
                                </div>
                                <div style={{ fontSize: "12px", color: "#666" }}>
                                  Description
                                </div>
                              </Col>
                              <Col span={3}>
                                <div style={{ fontWeight: 500 }}>
                                  {entitlement.quantity || 1}
                                </div>
                                <div style={{ fontSize: "12px", color: "#666" }}>
                                  Quantity
                                </div>
                              </Col>
                              <Col span={5}>
                                <div style={{ fontWeight: 500, color: "#52c41a" }}>
                                  ${(entitlement.amount || 0).toFixed(2)}
                                </div>
                                <div style={{ fontSize: "12px", color: "#666" }}>
                                  Amount
                                </div>
                              </Col>
                              <Col span={3}>
                                {currentFactura?.status !== "paid" &&
                                  currentFactura?.status !== "cancelled" && (
                                    <Popconfirm
                                      title="Remove Entitlement"
                                      description="Are you sure you want to remove this entitlement from the factura?"
                                      onConfirm={() =>
                                        handleRemoveEntitlement(entitlement.id)
                                      }
                                      okText="Yes"
                                      cancelText="No"
                                    >
                                      <DeleteOutlined
                                        style={{
                                          color: "#ff4d4f",
                                          cursor: "pointer",
                                          fontSize: "16px"
                                        }}
                                      />
                                    </Popconfirm>
                                  )}
                              </Col>
                            </Row>
                          </Card>
                        )
                      )}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#999",
                      fontStyle: "italic"
                    }}
                  >
                    No entitlements
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Tiers Section */}
        {isEdit && currentFactura && (
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                title="Tiers"
                className="factura-tiers-card"
                extra={
                  currentFactura?.status !== "paid" &&
                  currentFactura?.status !== "cancelled" && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setShowNewTierModal(true)}
                    >
                      + New Tier
                    </Button>
                  )
                }
              >
                {currentFactura.facturaDetails?.tiers &&
                currentFactura.facturaDetails.tiers.length > 0 ? (
                  <>
                    <div
                      className="tiers-summary"
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        background: "#f5f5f5",
                        borderRadius: 4
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <strong>Total Tiers: </strong>
                          {currentFactura.facturaDetails.tiers.length}
                        </Col>
                        <Col span={8}>
                          <strong>Total Amount: </strong>$
                          {currentFactura.facturaDetails.tiers
                            .reduce((sum, item) => sum + (item.amount || 0), 0)
                            .toFixed(2)}
                        </Col>
                      </Row>
                    </div>
                    <div className="tiers-list">
                      {currentFactura.facturaDetails.tiers.map((tier, index) => (
                        <Card
                          key={tier.id || index}
                          size="small"
                          className="tier-card"
                          style={{
                            marginBottom: 12,
                            border: "1px solid #d9d9d9"
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col span={4}>
                              <div
                                className="tier-thumbnail"
                                style={{
                                  width: 60,
                                  height: 60,
                                  backgroundColor: "#fff7e6",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "12px",
                                  color: "#fa8c16"
                                }}
                              >
                                Tier
                              </div>
                            </Col>
                            <Col span={9}>
                              <div style={{ fontWeight: 500 }}>
                                {tier.description || "Tier"}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Description
                              </div>
                            </Col>
                            <Col span={3}>
                              <div style={{ fontWeight: 500 }}>{tier.quantity || 1}</div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Quantity
                              </div>
                            </Col>
                            <Col span={5}>
                              <div style={{ fontWeight: 500, color: "#52c41a" }}>
                                ${(tier.amount || 0).toFixed(2)}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Amount
                              </div>
                            </Col>
                            <Col span={3}>
                              {currentFactura?.status !== "paid" &&
                                currentFactura?.status !== "cancelled" && (
                                  <Popconfirm
                                    title="Remove Tier"
                                    description="Are you sure you want to remove this tier from the factura?"
                                    onConfirm={() => handleRemoveTier(tier.id)}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <DeleteOutlined
                                      style={{
                                        color: "#ff4d4f",
                                        cursor: "pointer",
                                        fontSize: "16px"
                                      }}
                                    />
                                  </Popconfirm>
                                )}
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#999",
                      fontStyle: "italic"
                    }}
                  >
                    No tiers
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Charges Section */}
        {isEdit && currentFactura && (
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                title="Charges"
                className="factura-charges-card"
                extra={
                  currentFactura?.status !== "paid" &&
                  currentFactura?.status !== "cancelled" && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setShowNewChargeModal(true)}
                    >
                      + New Charge
                    </Button>
                  )
                }
              >
                {currentFactura.facturaDetails?.charges &&
                currentFactura.facturaDetails.charges.length > 0 ? (
                  <>
                    <div
                      className="charges-summary"
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        background: "#f5f5f5",
                        borderRadius: 4
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <strong>Total Charges: </strong>
                          {currentFactura.facturaDetails.charges.length}
                        </Col>
                        <Col span={8}>
                          <strong>Total Amount: </strong>$
                          {currentFactura.facturaDetails.charges
                            .reduce((sum, item) => sum + (item.amount || 0), 0)
                            .toFixed(2)}
                        </Col>
                      </Row>
                    </div>
                    <div className="charges-list">
                      {currentFactura.facturaDetails.charges.map((charge, index) => (
                        <Card
                          key={charge.id || index}
                          size="small"
                          className="charge-card"
                          style={{
                            marginBottom: 12,
                            border: "1px solid #d9d9d9"
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col span={4}>
                              <div
                                className="charge-thumbnail"
                                style={{
                                  width: 60,
                                  height: 60,
                                  backgroundColor: "#fff2e6",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "12px",
                                  color: "#fa8c16"
                                }}
                              >
                                Chg
                              </div>
                            </Col>
                            <Col span={9}>
                              <div style={{ fontWeight: 500 }}>
                                {charge.description || "Charge"}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Description
                              </div>
                            </Col>
                            <Col span={3}>
                              <div style={{ fontWeight: 500 }}>
                                {charge.quantity || 1}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Quantity
                              </div>
                            </Col>
                            <Col span={5}>
                              <div style={{ fontWeight: 500, color: "#52c41a" }}>
                                ${(charge.amount || 0).toFixed(2)}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Amount
                              </div>
                            </Col>
                            <Col span={3}>
                              {currentFactura?.status !== "paid" &&
                                currentFactura?.status !== "cancelled" && (
                                  <Popconfirm
                                    title="Remove Charge"
                                    description="Are you sure you want to remove this charge from the factura?"
                                    onConfirm={() => handleRemoveCharge(charge.id)}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <DeleteOutlined
                                      style={{
                                        color: "#ff4d4f",
                                        cursor: "pointer",
                                        fontSize: "16px"
                                      }}
                                    />
                                  </Popconfirm>
                                )}
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#999",
                      fontStyle: "italic"
                    }}
                  >
                    No charges
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Files Section */}
        {isEdit && currentFactura && (
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                title="Files"
                className="factura-files-card"
                extra={
                  <Upload
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    accept=".pdf,.docx,.xlsx,.xls,.doc,.csv,.ods,.odt,.jpg,.jpeg,.png,.webp,.heic"
                  >
                    <Button type="primary" size="small" icon={<PlusOutlined />}>
                      + New File
                    </Button>
                  </Upload>
                }
              >
                {currentFactura.facturaDetails?.files &&
                currentFactura.facturaDetails.files.length > 0 ? (
                  <>
                    <div
                      className="files-summary"
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        background: "#f5f5f5",
                        borderRadius: 4
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <strong>Total Files: </strong>
                          {currentFactura.facturaDetails.files.length}
                        </Col>
                        <Col span={8}>
                          <strong>Total Size: </strong>
                          {(
                            currentFactura.facturaDetails.files.reduce(
                              (sum, file) => sum + (file.size || 0),
                              0
                            ) / 1024
                          ).toFixed(1)}{" "}
                          KB
                        </Col>
                      </Row>
                    </div>
                    <div className="files-list">
                      {currentFactura.facturaDetails.files.map((file, index) => (
                        <Card
                          key={file.id || index}
                          size="small"
                          className="file-card"
                          style={{
                            marginBottom: 12,
                            border: "1px solid #d9d9d9"
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col span={4}>
                              <div
                                className="file-thumbnail"
                                style={{
                                  width: 60,
                                  height: 60,
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "20px"
                                }}
                              >
                                {file.mimeType &&
                                file.mimeType.startsWith("image/") &&
                                file.thumbnailUrl ? (
                                  <Image
                                    width={60}
                                    height={60}
                                    src={file.thumbnailUrl}
                                    style={{ objectFit: "cover", borderRadius: 4 }}
                                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8O+1ogo2nJOaC9oK2MZkgZxq7Q7YKgKwu5YZb1pQXkMjKKLyQE1wKhQCL2QJyI1g/YZcJzuvyqm8/t73fs8DoyNvPvfe3n19fnvn7O7pJGlKJgBAQCwqOBE/OisqqqqFUkBALKKJ+NFZUVFVLZQCAv5lN1bZpqN9cRXvvf6WkBNHH7xvJBQcSFBDgJwKAjSigYKXOQsF9VIWoKBiAKKgXFIWEJvq7du3s0WL1+7xoKDcxQL+++//Hb3b9nq9ym7lIzV68ebNm5Qlhh5TJAESIhAGECFGhERGj1cVVVUt1RIBSQCZJFwVOjhHfZI7pFSGhQP0OlQHKyUBAGD8H8KJQEEoFBShgBEhRooQAIQADQjZKIgGIwIIJdz//z8QJvBvKFABOTdEqQgCAaOLQIgOgH8iIVKPKAAEcGgooECBBBIqGiWQQDQSDQAQAwGFgIAqsJJNdGb7/AjQCBAAoWCCe6IhGhS8+c8AQoBwKCIQAAuCkMCgINVeNGhBqHj/CCAQigBAwBAQCAQQhhhThOhEBAQbMT4Bj2uO+AcAZKAMQ4D3YZoQAAQ/3jCTkGKAAFwGjpCQgUQAAgBhkBBHAhEJR7EKEBAACQD/6/t/9h+hIBQwjPo28qvs7++OPu/3hBQECBYCA2z1zHqGjGhRgACAACD+f6/uJb7/7x8hMJBQEP+///oAAEJhAABI//q/w//Z1GJCigQCQNGoCEwAgWQQQWcL6f4f/3oe6hpJ60CIhGJA8L+OgwDAj4yjJygWEEagRPGz31tPJCjgKCLKCCQCU5lEJCRIuEjCSgINPEJKfBCKPKOCA+P//e5TAECqoIgY1RgARVYKjABaQj1fgGPCAQ1O+I9tgACBAcH/n+H/PIDCy98==" // fallback image
                                  />
                                ) : (
                                  renderFileIcon(file.mimeType)
                                )}
                              </div>
                            </Col>
                            <Col span={14}>
                              <div style={{ fontWeight: 500 }}>
                                {file.originalName || file.name}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                {file.mimeType} - {(file.size / 1024).toFixed(1)} KB
                              </div>
                              <div style={{ fontSize: "12px", color: "#999" }}>
                                Uploaded:{" "}
                                {file.uploadedAt
                                  ? moment(file.uploadedAt).format("YYYY-MM-DD HH:mm")
                                  : "Unknown"}
                              </div>
                            </Col>
                            <Col span={6}>
                              <Space direction="vertical" style={{ width: "100%" }}>
                                <Button
                                  type="link"
                                  size="small"
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ padding: 0, height: "auto" }}
                                >
                                  View File
                                </Button>
                                <Popconfirm
                                  title="Are you sure you want to remove this file?"
                                  onConfirm={() =>
                                    handleFileRemove(file.id || file.fileId)
                                  }
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    style={{ padding: 0, height: "auto" }}
                                  >
                                    Remove
                                  </Button>
                                </Popconfirm>
                              </Space>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#999",
                      fontStyle: "italic"
                    }}
                  >
                    No files attached
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}

        <Modal
          title="Add Factura Log"
          open={showAddLogModal}
          onOk={handleAddLog}
          onCancel={() => {
            setShowAddLogModal(false);
            setNewLogNote("");
          }}
          confirmLoading={addingLog}
          okText="Add Log"
        >
          <Input.TextArea
            rows={4}
            placeholder="Enter log note..."
            value={newLogNote}
            onChange={(e) => setNewLogNote(e.target.value)}
          />
        </Modal>

        {/* Price Change Explanation Modal */}
        <Modal
          title="Price Change Explanation Required"
          open={showPriceExplanationModal}
          onCancel={handlePriceExplanationCancel}
          width={600}
          footer={null}
          // Keep this above the "All Routes" modal when a price is edited from there
          zIndex={1050}
        >
          <Form layout="vertical">
            <Form.Item
              label="Please explain why the final price is being changed:"
              required
            >
              <Input.TextArea
                rows={4}
                placeholder="Enter detailed explanation for the price change (e.g., additional waiting time, extra services, route modifications, discount applied, etc.)"
                maxLength={500}
                value={priceChangeExplanation}
                onChange={(e) => setPriceChangeExplanation(e.target.value)}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  onClick={handlePriceExplanationSubmit}
                  disabled={!priceChangeExplanation.trim()}
                >
                  Save Price Change
                </Button>
                <Button onClick={handlePriceExplanationCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Email Factura Modal */}
        <Modal
          title="Email Factura to Client"
          open={showEmailModal}
          onCancel={handleEmailModalCancel}
          width={600}
          footer={null}
        >
          <Form layout="vertical">
            <Form.Item label="Email Recipients">
              <div style={{ marginBottom: 16 }}>
                {selectedEmails.map((email, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      marginBottom: 8,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 4,
                      border: "1px solid #d9d9d9"
                    }}
                  >
                    <span>{email}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleRemoveEmail(email)}
                      style={{ color: "#ff4d4f" }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Input
                  placeholder="Enter additional email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onPressEnter={handleAddEmail}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleAddEmail}>Add</Button>
              </div>
            </Form.Item>

            <Form.Item>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={ccAccounting}
                  onChange={(e) => setCcAccounting(e.target.checked)}
                  id="ccAccounting"
                />
                <label htmlFor="ccAccounting">CC accounting@goflexio.com</label>
              </div>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  onClick={handleSendEmail}
                  loading={emailingFactura}
                  disabled={selectedEmails.length === 0}
                >
                  {emailingFactura ? "Sending..." : "Send Email"}
                </Button>
                <Button onClick={handleEmailModalCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* New Charge Modal */}
        <Modal
          title="Add New Charge"
          open={showNewChargeModal}
          onCancel={() => {
            setShowNewChargeModal(false);
            setNewChargeAmount("");
            setNewChargeDetail("");
          }}
          width={500}
          footer={null}
        >
          <Form layout="vertical">
            <Form.Item label="Charge Amount ($)" required>
              <InputNumber
                style={{ width: "100%" }}
                value={newChargeAmount}
                onChange={(value) => setNewChargeAmount(value)}
                min={0}
                max={99999999.99}
                precision={2}
                formatter={(value) =>
                  value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                }
                parser={(value) => value?.replace(/(,*)/g, "") || ""}
                placeholder="Enter charge amount"
              />
            </Form.Item>
            <Form.Item label="Charge Description" required>
              <Input.TextArea
                rows={3}
                value={newChargeDetail}
                onChange={(e) => setNewChargeDetail(e.target.value)}
                placeholder="Enter detailed description of the charge..."
                maxLength={200}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  loading={addingCharge}
                  disabled={!newChargeAmount || !newChargeDetail.trim()}
                  onClick={handleAddCharge}
                >
                  {addingCharge ? "Adding..." : "Add Charge"}
                </Button>
                <Button
                  onClick={() => {
                    setShowNewChargeModal(false);
                    setNewChargeAmount("");
                    setNewChargeDetail("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* New Entitlement Modal */}
        <Modal
          title="Add New Entitlement"
          open={showNewEntitlementModal}
          onCancel={() => {
            setShowNewEntitlementModal(false);
            setSelectedEntitlementId("");
            setEntitlementAmount("");
            setEntitlementSearchValue("");
          }}
          afterOpenChange={(open) => {
            if (open) {
              loadAvailableEntitlements();
            }
          }}
          width={500}
          footer={null}
        >
          <Form layout="vertical">
            <Form.Item label="Select Entitlement" required>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <AutoComplete
                  style={{ flex: 1 }}
                  placeholder={
                    loadingEntitlements
                      ? "Loading entitlements..."
                      : "Search and select an entitlement..."
                  }
                  value={entitlementSearchValue}
                  onChange={(value) => {
                    setEntitlementSearchValue(value);
                    // Find matching entitlement
                    const found = availableEntitlements.find(
                      (e) =>
                        (
                          e.name ||
                          e.description ||
                          `Entitlement ${e.id}`
                        ).toLowerCase() === value.toLowerCase()
                    );
                    setSelectedEntitlementId(found ? found.id : "");
                  }}
                  onSelect={(value) => {
                    setEntitlementSearchValue(value);
                    const found = availableEntitlements.find(
                      (e) => (e.name || e.description || `Entitlement ${e.id}`) === value
                    );
                    setSelectedEntitlementId(found ? found.id : "");
                  }}
                  filterOption={(inputValue, option) =>
                    option.value.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  options={availableEntitlements.map((entitlement) => ({
                    value:
                      entitlement.name ||
                      entitlement.description ||
                      `Entitlement ${entitlement.id}`,
                    label:
                      entitlement.name ||
                      entitlement.description ||
                      `Entitlement ${entitlement.id}`
                  }))}
                  notFoundContent={
                    loadingEntitlements ? "Loading..." : "No entitlements found"
                  }
                />
                <Button
                  type="default"
                  size="small"
                  loading={loadingEntitlements}
                  onClick={loadAvailableEntitlements}
                  title="Refresh entitlements"
                >
                  ↻
                </Button>
              </div>
            </Form.Item>
            <Form.Item label="Amount ($)" required>
              <InputNumber
                style={{ width: "100%" }}
                value={entitlementAmount}
                onChange={(value) => setEntitlementAmount(value)}
                min={0}
                max={99999999.99}
                precision={2}
                formatter={(value) =>
                  value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                }
                parser={(value) => value?.replace(/(,*)/g, "") || ""}
                placeholder="Enter entitlement amount"
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  loading={addingEntitlement}
                  disabled={!selectedEntitlementId || !entitlementAmount}
                  onClick={handleAddEntitlement}
                >
                  {addingEntitlement ? "Adding..." : "Add Entitlement"}
                </Button>
                <Button
                  onClick={() => {
                    setShowNewEntitlementModal(false);
                    setSelectedEntitlementId("");
                    setEntitlementAmount("");
                    setEntitlementSearchValue("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* New Tier Modal */}
        <Modal
          title="Add New Tier"
          open={showNewTierModal}
          onCancel={() => {
            setShowNewTierModal(false);
            setSelectedTierId("");
            setTierAmount("");
            setTierSearchValue("");
          }}
          afterOpenChange={(open) => {
            if (open) {
              loadAvailableTiers();
            }
          }}
          width={500}
          footer={null}
        >
          <Form layout="vertical">
            <Form.Item label="Select Tier" required>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <AutoComplete
                  style={{ flex: 1 }}
                  placeholder={
                    loadingTiers ? "Loading tiers..." : "Search and select a tier..."
                  }
                  value={tierSearchValue}
                  onChange={(value) => {
                    setTierSearchValue(value);
                    // Find matching tier
                    const found = availableTiers.find(
                      (t) =>
                        (t.name || `Tier ${t.id}`).toLowerCase() === value.toLowerCase()
                    );
                    if (found) {
                      setSelectedTierId(found.id);
                      setTierAmount(found.cost || 0);
                    } else {
                      setSelectedTierId("");
                    }
                  }}
                  onSelect={(value) => {
                    setTierSearchValue(value);
                    const found = availableTiers.find(
                      (t) => (t.name || `Tier ${t.id}`) === value
                    );
                    if (found) {
                      setSelectedTierId(found.id);
                      setTierAmount(found.cost || 0);
                    } else {
                      setSelectedTierId("");
                    }
                  }}
                  filterOption={(inputValue, option) =>
                    option.value.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  options={availableTiers.map((tier) => ({
                    value: tier.name || `Tier ${tier.id}`,
                    label: tier.name || `Tier ${tier.id}`
                  }))}
                  notFoundContent={loadingTiers ? "Loading..." : "No tiers found"}
                />
                <Button
                  type="default"
                  size="small"
                  loading={loadingTiers}
                  onClick={loadAvailableTiers}
                  title="Refresh tiers"
                >
                  ↻
                </Button>
              </div>
            </Form.Item>
            <Form.Item label="Amount ($)" required>
              <InputNumber
                style={{ width: "100%" }}
                value={tierAmount}
                onChange={(value) => setTierAmount(value)}
                min={0}
                max={99999999.99}
                precision={2}
                formatter={(value) =>
                  value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                }
                parser={(value) => value?.replace(/(,*)/g, "") || ""}
                placeholder="Enter tier amount"
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  loading={addingTier}
                  disabled={!selectedTierId || !tierAmount}
                  onClick={handleAddTier}
                >
                  {addingTier ? "Adding..." : "Add Tier"}
                </Button>
                <Button
                  onClick={() => {
                    setShowNewTierModal(false);
                    setSelectedTierId("");
                    setTierAmount("");
                    setTierSearchValue("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* New File Upload Modal */}
        <Modal
          title="Upload File to Factura"
          open={showNewFileModal}
          onCancel={() => {
            setShowNewFileModal(false);
            setSelectedFile(null);
          }}
          width={500}
          footer={null}
        >
          <div style={{ marginBottom: 16 }}>
            <p>
              <strong>File Details:</strong>
            </p>
            {selectedFile && (
              <div
                style={{
                  padding: 12,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  marginBottom: 16
                }}
              >
                <div>
                  <strong>Name:</strong> {selectedFile.name}
                </div>
                <div>
                  <strong>Size:</strong> {(selectedFile.size / 1024).toFixed(1)} KB
                </div>
                <div>
                  <strong>Type:</strong> {selectedFile.type}
                </div>
              </div>
            )}

            <div
              style={{
                padding: 12,
                backgroundColor: "#e6f7ff",
                borderRadius: 4,
                marginBottom: 16,
                fontSize: "12px"
              }}
            >
              <p style={{ margin: 0, marginBottom: 8 }}>
                <strong>Allowed file types:</strong>
              </p>
              <p style={{ margin: 0 }}>
                • Documents: PDF, XLSX, DOCX, CSV, ODS, ODT
                <br />
                • Images: JPG, PNG, WebP, HEIC
                <br />• Max size: 5MB
              </p>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <Space>
              <Button
                onClick={() => {
                  setShowNewFileModal(false);
                  setSelectedFile(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={uploadingFile}
                disabled={!selectedFile}
                onClick={handleFileUpload}
                icon={<UploadOutlined />}
              >
                {uploadingFile ? "Uploading..." : "Upload File"}
              </Button>
            </Space>
          </div>
        </Modal>

        {/* Add Routes Modal */}
        <Modal
          title="Add Routes to Factura"
          open={showAddRoutesModal}
          onCancel={() => {
            setShowAddRoutesModal(false);
            setSelectedRoutes([]);
            setRouteSearchValue("");
          }}
          afterOpenChange={(open) => {
            if (open) {
              console.log("Routes modal opened, attempting to load routes...");
              console.log("Current factura:", currentFactura);
              console.log("Form client ID:", form.getFieldValue("clientId"));
              loadAvailableRoutes();
            }
          }}
          width={800}
          footer={null}
        >
          <Form layout="vertical">
            <Form.Item label="Search and Select Routes" required>
              <div
                style={{
                  marginBottom: 12,
                  padding: 8,
                  backgroundColor: "#f6f8ff",
                  border: "1px solid #d1d9ff",
                  borderRadius: 4
                }}
              >
                <strong>Reminder:</strong> We recommend adding routes via the{" "}
                <a href="/admin/routes" target="_blank">
                  Active Routes screen
                </a>{" "}
                for better route details and management.
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 16
                }}
              >
                <Spin spinning={loadingRoutes} style={{ flex: 1 }}>
                  <AutoComplete
                    style={{ width: "100%" }}
                    placeholder={
                      loadingRoutes
                        ? "Loading routes..."
                        : "Search by route ID, status, or price..."
                    }
                    value={routeSearchValue}
                    onChange={(value) => setRouteSearchValue(value)}
                    onSelect={(value) => {
                      const selected = availableRoutes.find(
                        (route) =>
                          `${route.id.substring(0, 8).toUpperCase()} - ${
                            route.status
                          } - $${parseFloat(route.clientFinalPrice || 0).toFixed(2)}` ===
                          value
                      );
                      if (selected && !selectedRoutes.includes(selected.id)) {
                        setSelectedRoutes([...selectedRoutes, selected.id]);
                        setRouteSearchValue("");
                      }
                    }}
                    filterOption={(inputValue, option) =>
                      option.value.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={availableRoutes
                      .filter((route) => !selectedRoutes.includes(route.id))
                      .map((route) => ({
                        value: `${route.id.substring(0, 8).toUpperCase()} - ${
                          route.status
                        } - $${parseFloat(route.clientFinalPrice || 0).toFixed(2)}`,
                        label: (
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {route.id.substring(0, 8).toUpperCase()}
                            </div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              Status: {route.status} | Price: $
                              {parseFloat(route.clientFinalPrice || 0).toFixed(2)}
                            </div>
                          </div>
                        )
                      }))}
                    notFoundContent={
                      loadingRoutes ? <Spin size="small" /> : "No routes found"
                    }
                    disabled={loadingRoutes}
                  />
                </Spin>
                <Button
                  type="default"
                  size="small"
                  loading={loadingRoutes}
                  onClick={loadAvailableRoutes}
                  title="Refresh routes"
                >
                  ↻
                </Button>
              </div>

              {/* Selected Routes Display */}
              {selectedRoutes.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    Selected Routes ({selectedRoutes.length}):
                  </div>
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {selectedRoutes.map((routeId) => {
                      const route = availableRoutes.find((r) => r.id === routeId);
                      if (!route) return null;
                      return (
                        <div
                          key={routeId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 8,
                            backgroundColor: "#e6f7ff",
                            borderRadius: 4,
                            marginBottom: 4
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {route.id.substring(0, 8).toUpperCase()}
                            </div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              {route.status} - $
                              {parseFloat(route.clientFinalPrice || 0).toFixed(2)}
                            </div>
                          </div>
                          <Button
                            type="text"
                            size="small"
                            onClick={() => {
                              setSelectedRoutes(
                                selectedRoutes.filter(
                                  (routeFilterId) => routeFilterId !== routeId
                                )
                              );
                            }}
                            style={{ color: "#ff4d4f" }}
                          >
                            Remove
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  disabled={selectedRoutes.length === 0}
                  onClick={handleAddRoutes}
                >
                  Add {selectedRoutes.length} Route
                  {selectedRoutes.length !== 1 ? "s" : ""}
                </Button>
                <Button
                  onClick={() => {
                    setShowAddRoutesModal(false);
                    setSelectedRoutes([]);
                    setRouteSearchValue("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Add Pool Packages Modal */}
        <Modal
          title="Add Pool Packages to Factura"
          open={showAddPoolPackagesModal}
          onCancel={() => {
            setShowAddPoolPackagesModal(false);
            setSelectedPoolPackages([]);
            setPoolPackageSearchValue("");
          }}
          afterOpenChange={(open) => {
            if (open) {
              loadAvailablePoolPackages();
            }
          }}
          width={800}
          footer={null}
        >
          <Form layout="vertical">
            <Form.Item label="Search and Select Pool Packages" required>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 16
                }}
              >
                <AutoComplete
                  style={{ flex: 1 }}
                  placeholder={
                    loadingPoolPackages
                      ? "Loading pool packages..."
                      : "Search by package ID, status, or address..."
                  }
                  value={poolPackageSearchValue}
                  onChange={(value) => setPoolPackageSearchValue(value)}
                  onSelect={(value) => {
                    const selected = availablePoolPackages.find(
                      (pkg) =>
                        `${pkg.id.substring(0, 8).toUpperCase()} - ${pkg.status}` ===
                        value.split(" - ")[0] + " - " + value.split(" - ")[1]
                    );
                    if (selected && !selectedPoolPackages.includes(selected.id)) {
                      setSelectedPoolPackages([...selectedPoolPackages, selected.id]);
                      setPoolPackageSearchValue("");
                    }
                  }}
                  filterOption={(inputValue, option) =>
                    option.value.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  options={availablePoolPackages
                    .filter((pkg) => !selectedPoolPackages.includes(pkg.id))
                    .map((pkg) => ({
                      value: `${pkg.id.substring(0, 8).toUpperCase()} - ${pkg.status} - ${
                        pkg.pickupAddress
                          ? pkg.pickupAddress.substring(0, 30) + "..."
                          : "N/A"
                      }`,
                      label: (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%"
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500 }}>
                              {pkg.id.substring(0, 8).toUpperCase()}
                            </div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              Status: {pkg.status}
                            </div>
                          </div>
                          <div style={{ flex: 2, marginLeft: 16 }}>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              <strong>Pickup:</strong>{" "}
                              {pkg.pickupStopName || pkg.pickupAddress || "N/A"}
                            </div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              <strong>Dropoff:</strong>{" "}
                              {pkg.dropoffStopName || pkg.dropoffAddress || "N/A"}
                            </div>
                          </div>
                          <div style={{ flex: 1, marginLeft: 16 }}>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              <strong>Type:</strong>{" "}
                              {getPackageTypeName(
                                pkg.packageV2?.package_type_id || pkg.packageTypeId
                              ) ||
                                pkg.packageType ||
                                pkg.size ||
                                "N/A"}
                            </div>
                          </div>
                        </div>
                      )
                    }))}
                  notFoundContent={
                    loadingPoolPackages ? "Loading..." : "No pool packages found"
                  }
                />
                <Button
                  type="default"
                  size="small"
                  loading={loadingPoolPackages}
                  onClick={loadAvailablePoolPackages}
                  title="Refresh pool packages"
                >
                  ↻
                </Button>
              </div>

              {/* Selected Pool Packages Display */}
              {selectedPoolPackages.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    Selected Pool Packages ({selectedPoolPackages.length}):
                  </div>
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {selectedPoolPackages.map((packageId) => {
                      const pkg = availablePoolPackages.find((p) => p.id === packageId);
                      if (!pkg) return null;
                      return (
                        <div
                          key={packageId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 8,
                            backgroundColor: "#e6f7ff",
                            borderRadius: 4,
                            marginBottom: 4
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, marginBottom: 4 }}>
                              {pkg.id.substring(0, 8).toUpperCase()}
                            </div>
                            <div style={{ fontSize: 11, color: "#666", lineHeight: 1.3 }}>
                              <div>
                                <strong>Status:</strong> {pkg.status}
                              </div>
                              <div>
                                <strong>Pickup:</strong>{" "}
                                {pkg.pickupStopName || pkg.pickupAddress || "N/A"}
                              </div>
                              <div>
                                <strong>Dropoff:</strong>{" "}
                                {pkg.dropoffStopName || pkg.dropoffAddress || "N/A"}
                              </div>
                              <div>
                                <strong>Type:</strong>{" "}
                                {getPackageTypeName(
                                  pkg.packageV2?.package_type_id || pkg.packageTypeId
                                ) ||
                                  pkg.packageType ||
                                  pkg.size ||
                                  "N/A"}{" "}
                                | <strong>Date:</strong>{" "}
                                {pkg.packageV2?.pickupDate || pkg.packageV2PickupDate
                                  ? new Date(
                                      pkg.packageV2?.pickupDate || pkg.packageV2PickupDate
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </div>
                            </div>
                          </div>
                          <Button
                            type="text"
                            size="small"
                            onClick={() => {
                              setSelectedPoolPackages(
                                selectedPoolPackages.filter(
                                  (pkgFilterId) => pkgFilterId !== packageId
                                )
                              );
                            }}
                            style={{ color: "#ff4d4f" }}
                          >
                            Remove
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  disabled={selectedPoolPackages.length === 0}
                  onClick={handleAddPoolPackages}
                >
                  Add {selectedPoolPackages.length} Pool Package
                  {selectedPoolPackages.length !== 1 ? "s" : ""}
                </Button>
                <Button
                  onClick={() => {
                    setShowAddPoolPackagesModal(false);
                    setSelectedPoolPackages([]);
                    setPoolPackageSearchValue("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* All Routes Modal */}
        <Modal
          title={`All Routes (${currentFactura?.facturaDetails?.routes?.length || 0})`}
          open={showAllRoutesModal}
          onCancel={() => setShowAllRoutesModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowAllRoutesModal(false)}>
              Close
            </Button>
          ]}
          width={900}
        >
          <div className="routes-list" style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {currentFactura?.facturaDetails?.routes?.map((route) =>
              renderRouteCard(route, { inModal: true })
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default FacturaForm;
